import { zodResolver } from "@hookform/resolvers/zod";
import React, { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { GbpCategoriesInput } from "./GbpCategoriesInput";
import { CategoryAutocompleter } from "./GbpCategoriesInput/CategoryAutocompleter";
import { generateDefaultValues, schema, Schema } from "./schema";
import useRefreshLocationData, {
  RefreshType,
} from "../../../../containers/FacilityDetailsPage/hooks/useRefreshLocationData";
import { ConfirmCloseContext } from "../../../../contexts/confirmClose";
import { LocationData } from "../../../../models/LocationData";
import DataSvc from "../../../../services/dataSvc";
import { HookForm } from "../../../HookForm/HookForm";

import "./styles.scss";

export interface ICategorySectionProps {
  locationData: LocationData;
  accordionEventKey: string;
}

export const CategorySection = (props: ICategorySectionProps) => {
  const { locationData, accordionEventKey } = props;

  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityDetailsPage.modalEditProfile.categoryForm.${key}`);

  const refresh = useRefreshLocationData(locationData.id.toString());

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: generateDefaultValues(locationData),
    mode: "onChange",
  });

  const onSubmit = async (data: Schema) => {
    const isvalid = await form.trigger();
    if (!isvalid) {
      return;
    }

    const payload = await CategoryAutocompleter.createUpdatePayload(data);
    try {
      await DataSvc.updateLocationProfile(locationData.id, payload);
      toast.success(t("success"));
      refresh(RefreshType.LOCATION);
      form.reset(data);
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  const { register, unregister } = useContext(ConfirmCloseContext);

  const disableSubmit =
    form.formState.errors.additionalCategories !== undefined;

  useEffect(() => {
    if (form.formState.isDirty) {
      register({
        key: accordionEventKey,
        message: "dirty",
        onCloseCallback: form.reset,
      });
    } else {
      unregister(accordionEventKey);
    }
  }, [form.formState.isDirty, register, unregister, accordionEventKey, form]);

  return (
    <HookForm
      onSubmit={onSubmit}
      form={form}
      submitButtonLabel={t("update")}
      disableSubmit={disableSubmit}
    >
      <GbpCategoriesInput locationData={locationData} />
    </HookForm>
  );
};
